import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { axiosInstance, getBpmPrefix } from "../../../api/axios-client";
import { getDownloadTermByIdUrl } from "../../../api/bpm/endpoints";
import type { TermsOfUseDto } from "../../../api/bpm/model";

interface TermsOfUseFormProps {
  latestTerm?: TermsOfUseDto;
  isPublishing?: boolean;
  onPublish: (input: { name: string; file: File }) => void | Promise<void>;
}

export default function TermsOfUseForm({
  latestTerm,
  isPublishing = false,
  onPublish,
}: TermsOfUseFormProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File>();
  const [nameError, setNameError] = useState(false);
  const [fileError, setFileError] = useState<string>();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloadingTerm, setIsDownloadingTerm] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [pdfError, setPdfError] = useState<string>();

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadTermBlob = async (termId: string): Promise<Blob> => {
    const bpmPrefix = getBpmPrefix();
    const url = getDownloadTermByIdUrl(termId);
    const response = await axiosInstance.get(`${bpmPrefix}${url}`, {
      responseType: "blob",
    });
    return new Blob([response.data], { type: "application/pdf" });
  };

  const handleViewTerm = async () => {
    if (!latestTerm?.id) return;
    setIsPreviewOpen(true);
    if (previewUrl) return;

    setIsViewing(true);
    setPdfError(undefined);
    try {
      const blob = await loadTermBlob(latestTerm.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      setPdfError(t("admin.termsOfUse.downloadError"));
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownloadTerm = async () => {
    if (!latestTerm?.id) return;

    setIsDownloadingTerm(true);
    try {
      let url = previewUrl;
      let tempUrl: string | undefined;

      if (!url) {
        const blob = await loadTermBlob(latestTerm.id);
        tempUrl = URL.createObjectURL(blob);
        url = tempUrl;
      }

      const link = document.createElement("a");
      link.href = url;
      const fileName = `${latestTerm.name || "termo_de_uso"}.pdf`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (tempUrl) {
        window.URL.revokeObjectURL(tempUrl);
      }
    } catch {
      setPdfError(t("admin.termsOfUse.downloadError"));
    } finally {
      setIsDownloadingTerm(false);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleFileChange = (selectedFile?: File) => {
    if (!selectedFile) {
      setFile(undefined);
      return;
    }

    const isPdf =
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setFile(undefined);
      setFileError(t("admin.termsOfUse.pdfOnly"));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setFile(selectedFile);
    setFileError(undefined);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const hasName = Boolean(name.trim());
    setNameError(!hasName);

    if (!file) {
      setFileError(t("admin.termsOfUse.fileRequired"));
    }

    if (!hasName || !file) return;
    void onPublish({ name: name.trim(), file });
  };

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">
              {t("admin.termsOfUse.currentTerm")}
            </Typography>
            {latestTerm ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                >
                  <Typography fontWeight={600}>{latestTerm.name}</Typography>
                  <Chip
                    label={t("admin.termsOfUse.version", {
                      version: latestTerm.version,
                    })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
                {latestTerm.id && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        isViewing ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <VisibilityIcon />
                        )
                      }
                      onClick={handleViewTerm}
                      disabled={isViewing || isDownloadingTerm}
                    >
                      {t("common.view")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        isDownloadingTerm ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      onClick={handleDownloadTerm}
                      disabled={isViewing || isDownloadingTerm}
                    >
                      {t("common.download")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Alert severity="info">
                {t("admin.termsOfUse.noPublishedTerm")}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {latestTerm
                    ? t("admin.termsOfUse.publishNewVersion")
                    : t("admin.termsOfUse.publishFirstTerm")}
                </Typography>
                <Typography color="text.secondary">
                  {t("admin.termsOfUse.publicationHint")}
                </Typography>
              </Box>

              <Divider />

              <TextField
                label={t("admin.termsOfUse.name")}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (event.target.value.trim()) setNameError(false);
                }}
                error={nameError}
                helperText={
                  nameError ? t("admin.termsOfUse.nameRequired") : undefined
                }
                disabled={isPublishing}
                required
                fullWidth
              />

              <Stack spacing={1} alignItems="flex-start">
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  disabled={isPublishing}
                >
                  {t("admin.termsOfUse.selectPdf")}
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) =>
                      handleFileChange(event.target.files?.[0])
                    }
                  />
                </Button>
                {file && <Typography variant="body2">{file.name}</Typography>}
                {fileError && (
                  <Typography variant="caption" color="error">
                    {fileError}
                  </Typography>
                )}
              </Stack>

              <Alert severity="warning">
                {t("admin.termsOfUse.acceptanceWarning")}
              </Alert>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isPublishing}
                  startIcon={
                    isPublishing ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                >
                  {latestTerm
                    ? t("admin.termsOfUse.publishNewVersion")
                    : t("admin.termsOfUse.publishFirstTerm")}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {latestTerm?.id && (
        <Dialog
          open={isPreviewOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { height: "85vh", maxHeight: "85vh" },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="span" fontWeight={600}>
              {latestTerm.name}{" "}
              {latestTerm.version !== undefined &&
                `(${t("admin.termsOfUse.version", { version: latestTerm.version })})`}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClosePreview}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              p: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isViewing ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  gap: 2,
                  p: 4,
                }}
              >
                <CircularProgress />
                <Typography color="text.secondary">
                  {t("terms.loadingTerm")}
                </Typography>
              </Box>
            ) : pdfError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  p: 4,
                }}
              >
                <Alert severity="error">{pdfError}</Alert>
              </Box>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                title={latestTerm.name || t("admin.termsOfUse.currentTerm")}
                width="100%"
                height="100%"
                style={{ border: "none", flex: 1 }}
              />
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={
                isDownloadingTerm ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownloadTerm}
              disabled={isDownloadingTerm}
            >
              {t("common.download")}
            </Button>
            <Button onClick={handleClosePreview}>{t("common.close")}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

